import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useInjection } from '@src/components/Injection';
import { auth } from '@src/firebase/firebase';
import { CLIENT_SERVICE, ClientService } from '@src/services/client.service';

interface UserObject {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  status: string;
  role: string;
  clinicId: number;
}

interface AuthContextType {
  currentUser: any;
  userLoggedIn: boolean;
  loading: boolean;
  userObject: UserObject;
  initializeUser?: (user: any, service: any) => Promise<void>;
}

const initialUserObject: UserObject = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  status: '',
  role: '',
  clinicId: 0,
};

const AuthContext = React.createContext<AuthContextType>({
  currentUser: auth.currentUser,
  userLoggedIn: auth.currentUser ? true : false,
  loading: true,
  userObject: initialUserObject,
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoggedIn, setUserLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userObject, setUserObject] = useState(initialUserObject);

  const clientService = useInjection<ClientService>(CLIENT_SERVICE);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setLoading(true);
      if (user) {
        await initializeUser(user, clientService);
      } else {
        setCurrentUser(null);
        setUserLoggedIn(false);
        setUserObject(initialUserObject);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const fetchUserData = async (user, service) => {
    try {
      const userInfo = await service.getByUUID(user.uid);

      await setUserObject({
        firstName: userInfo.first_name,
        lastName: userInfo.last_name,
        email: userInfo.email,
        phone: userInfo.phone,
        status: userInfo.status,
        role: userInfo.role,
        clinicId: userInfo.clinic_id,
      });
    } catch (err) {
      console.error('Failed to fetch user data: ', err);
    }
  };

  async function initializeUser(user, service) {
    if (user) {
      setCurrentUser({ ...user });
      setUserLoggedIn(true);
      await fetchUserData(user, service);
    } else {
      setCurrentUser(null);
      setUserLoggedIn(false);
      setUserObject(null);
    }
  }

  const value = {
    currentUser,
    userLoggedIn,
    loading,
    userObject,
    initializeUser,
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
